<template>
  <div class="content-block">
    <!-- Header -->
    <div
      v-html="renderedHeader"
      class="rendered-content ml-14 pl-4 text-font-primary mt-8 prose"
    ></div>

    <!-- Search Component -->
    <RoamingRatesSearch
      :tariffData="tariffData"
      @select-country="selectCountry"
    />

    <!-- Selected Country Details -->
    <RoamingRatesCountry v-if="selectedCountry" :country="selectedCountry" />

    <!-- Show Roaming rates on ships, flights & satellite phones -->
    <RoamingRatesCountry
      v-if="showShipsSataliteZone"
      :country="shipsSataliteZone"
    />

    <!-- Main Rates Component (only when no country is selected) -->
    <RoamingRates
      v-if="!selectedCountry && tariffData"
      :tariffData="tariffData"
      @select-country="selectCountry"
    />

    <!-- Footer -->
    <div
      v-html="renderedFooter"
      class="rendered-content ml-14 pl-4 text-font-primary mt-8 prose w-11/12 sm:w-full md:w-5/6 lg:w-7/12 xl:w-7/12"
    ></div>

    <!-- Main Rates Component (only when no country is selected) -->
    <RoamingRates
      v-if="selectedCountry && tariffData"
      :tariffData="tariffData"
      @select-country="selectCountry"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import MarkdownIt from "markdown-it";

// Components

const { tariff } = useBackendService();

// Props
const props = defineProps({
  content: {
    type: Object,
    required: true,
    default: () => ({
      header: "",
      footer: "",
    }),
  },
});

// State
const tariffData = ref(null);
const selectedCountry = ref(null);
const shipsSataliteZone = ref(null);
const showShipsSataliteZone = ref(false);

// Markdown renderer
const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: true,
});

const renderedContent = (content) => {
  let cleanContent = (content || "")
    .split("\n")
    .map((line) => {
      // Convert bullet points to dashes while preserving indentation
      return line.replace(/^(\s*)•\s/, "$1- ");
    })
    .join("\n");

  // Find and format phone number links
  // Example: Call [02 5126 0000](tel:02 5126 0000)
  cleanContent = cleanContent.replace(
    /\[(\d[\d\s]+)\]\(tel:[\d\s]+\)/g,
    (match, phone) => {
      const cleanPhone = phone.replace(/\s+/g, "");
      return `[${phone}](tel:${cleanPhone})`;
    }
  );

  return md.render(cleanContent);
};

const renderedHeader = computed(() => renderedContent(props.content?.header));
const renderedFooter = computed(() => renderedContent(props.content?.footer));

// Methods
const fetchTariffData = async () => {
  try {
    const result = await tariff();
    tariffData.value = result.body;

    // Find ships & satellite zones
    shipsSataliteZone.value = tariffData.value?.zones?.find(
      (z) => z.countries.length === 0
    );

    // Start checking for link after data is loaded
    let attempts = 0;
    const maxAttempts = 3;

    const checkAndAddEventListener = () => {
      const fleetLink = document.querySelector(
        'a[href="#roaming-rates-fleet-sec"]'
      );
      if (fleetLink) {
        fleetLink.addEventListener("click", (e) => {
          e.preventDefault();
          showShipsSataliteZone.value = true;
        });
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkAndAddEventListener, 1000); // Try again every second
      }
    };

    checkAndAddEventListener();
  } catch (error) {
    console.error("Error fetching tariff data:", error);
  }
};

const selectCountry = (country) => {
  if (!country) {
    selectedCountry.value = null;
    return;
  }

  const zone = tariffData.value?.zones?.find((z) =>
    z.countries.some((c) => c.id === country.id)
  );

  selectedCountry.value = {
    ...country,
    tariff: zone?.tariff,
  };

  // Scroll to top smoothly
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

// Lifecycle
onMounted(() => {
  fetchTariffData();
});
</script>

<style scoped>
.rendered-content {
  :deep(h4) {
    font-size: 18px;
    font-weight: 700;
    line-height: 27px;
    margin-bottom: 8px;
  }
}
.content-block {
  @apply bg-white max-w-[1430px] m-auto;
}
.prose {
}
</style>
